import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format, startOfMonth } from "date-fns";
import { Calendar, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@/components/ui/data-table";
import { DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
	type MobileFilterChip,
	MobileFilterField,
	MobileFilters,
} from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { PharmacySelector } from "@/features/reports/pharmacy-selector";
import { type MarginDistributionRow, reportsApi } from "@/features/reports/reports-api";
import {
	dateRangeLabel,
	PADDED_FILTER_BAR_CLASS,
	showCountLabel,
} from "@/features/reports/sprz-mobile-filters";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { RODZAJ_OPTIONS } from "@/lib/rodzaj-leku";
import { ensureElementVisible } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

// Native HTML5 <input type="date"> commituje value gdy rok ma 4 cyfry —
// bez debounce posrednie daty (np. rok 0205 zanim user dotypuje 2025) leca
// do backendu i moga rzucic 500. Patrz Sentry 115094621 (zwroty-kwartalne).
const DATE_INPUT_DEBOUNCE_MS = 500;

const DESKTOP_DATE_INPUT_CLASS = "w-36 h-8 border-0 bg-transparent text-sm focus-visible:ring-0";

export const Route = createFileRoute("/_auth/raporty/rozklad-marz")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.raporty.rozklad-marz",
			location.pathname,
		);
	},
	component: MarginDistributionPage,
});

// Kolumny statyczne (bez domknięć na stan) — module scope, stabilna referencja
// nie unieważnia wewnętrznych memo DataTable przy re-renderach strony.
// Telefon: tytulem jest towar, podtytulem data · BLOZ · rodzaj; raport dotyczy jednej
// apteki, wiec jej tozsamosc trafia do szczegolow. `mobileLabel` wszedzie, bo tabela
// nie ma `columnLabels`, a naglowki sa funkcjami.
const columns: ColumnDef<MarginDistributionRow>[] = [
	{
		accessorKey: "kierownik",
		header: ({ column }) => <DataTableColumnHeader column={column} title="KR" />,
		size: 140,
		meta: { filterMode: "smart", mobileRole: "detail", mobileLabel: "KR" },
	},
	{
		accessorKey: "nazwaApteki",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Apteka" />,
		size: 200,
		meta: { filterMode: "smart", mobileRole: "detail", mobileLabel: "Apteka" },
	},
	{
		accessorKey: "ksId",
		header: ({ column }) => <DataTableColumnHeader column={column} title="KS ID" />,
		size: 90,
		cell: ({ getValue }) => (
			<span className="font-mono text-muted-foreground">{getValue() as string}</span>
		),
		meta: { mobileRole: "detail", mobileLabel: "KS ID" },
	},
	{
		accessorKey: "dataSprz",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Data" />,
		size: 100,
		cell: ({ getValue }) => {
			const val = getValue();
			return val ? format(new Date(val as string), "yyyy-MM-dd") : "—";
		},
		meta: { mobileRole: "subtitle", mobileLabel: "Data" },
	},
	{
		accessorKey: "nazwaTowaru",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Nazwa Towaru" />,
		size: 350,
		meta: { filterMode: "smart", mobileRole: "title", mobileLabel: "Nazwa Towaru" },
	},
	{
		accessorKey: "bloz07",
		header: ({ column }) => <DataTableColumnHeader column={column} title="BLOZ" />,
		size: 100,
		meta: { mobileRole: "subtitle", mobileLabel: "BLOZ" },
	},
	{
		accessorKey: "rodzajLeku",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Rodzaj" />,
		size: 80,
		meta: { facetedOptions: RODZAJ_OPTIONS, mobileRole: "subtitle", mobileLabel: "Rodzaj" },
	},
	{
		accessorKey: "vatsp",
		header: ({ column }) => <DataTableColumnHeader column={column} title="VAT" isNumeric />,
		size: 60,
		cell: ({ getValue }) => `${getValue()}%`,
		// Stawka, nie miara — tylko w szczegolach wiersza.
		meta: { isNumeric: true, mobileRole: "detail", mobileLabel: "VAT" },
	},
	{
		accessorKey: "sprzIlospLacznie",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Ilość" isNumeric />,
		size: 80,
		cell: ({ getValue }) => (getValue() as number).toFixed(2),
		meta: { isNumeric: true, mobileLabel: "Ilość" },
	},
	{
		accessorKey: "sprzNettoLacznie",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Netto Sprz." isNumeric />,
		size: 110,
		cell: ({ getValue }) => `${(getValue() as number).toFixed(2)} zł`,
		meta: { isNumeric: true, mobileLabel: "Netto Sprz." },
	},
	{
		accessorKey: "warZakNettoLacznie",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Netto Zak." isNumeric />,
		size: 110,
		cell: ({ getValue }) => `${(getValue() as number).toFixed(2)} zł`,
		meta: { isNumeric: true, mobileLabel: "Netto Zak." },
	},
	{
		accessorKey: "marzaLacznie",
		header: ({ column }) => <DataTableColumnHeader column={column} title="Marża" isNumeric />,
		size: 110,
		cell: ({ getValue }) => {
			const val = getValue() as number;
			return (
				<span className={val < 0 ? "text-red-500 font-bold" : "text-green-600"}>
					{val.toFixed(2)} zł
				</span>
			);
		},
		meta: { isNumeric: true, mobileDefaultMeasure: true, mobileLabel: "Marża" },
	},
	{
		accessorKey: "sredniaCenaSprzNetto",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Śr. Cena Sprz." isNumeric />
		),
		size: 110,
		cell: ({ getValue }) => `${(getValue() as number).toFixed(2)} zł`,
		meta: { isNumeric: true, mobileLabel: "Śr. Cena Sprz." },
	},
	{
		accessorKey: "sredniaCenaZakNetto",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Śr. Cena Zak." isNumeric />
		),
		size: 110,
		cell: ({ getValue }) => `${(getValue() as number).toFixed(2)} zł`,
		meta: { isNumeric: true, mobileLabel: "Śr. Cena Zak." },
	},
];

function MarginDistributionPage() {
	const today = new Date();
	const firstDayOfMonth = startOfMonth(today);

	const [idFirm, setIdFirm] = useState<number | null>(null);
	// Nazwa wybranej apteki tylko do chipa filtrow na telefonie.
	const [pharmacyName, setPharmacyName] = useState<string | null>(null);
	const isBelowMd = useIsBelowMd();
	const [dateFrom, setDateFrom] = useState<string>(format(firstDayOfMonth, "yyyy-MM-dd"));
	const [dateTo, setDateTo] = useState<string>(format(today, "yyyy-MM-dd"));

	const debouncedDateFrom = useDebounce(dateFrom, DATE_INPUT_DEBOUNCE_MS);
	const debouncedDateTo = useDebounce(dateTo, DATE_INPUT_DEBOUNCE_MS);

	const { data: reportData = [], isFetching } = useQuery({
		queryKey: ["reports", "margin-distribution", idFirm, debouncedDateFrom, debouncedDateTo],
		queryFn: () =>
			reportsApi.getMarginDistribution({
				idFirm: idFirm as number,
				dateFrom: debouncedDateFrom,
				dateTo: debouncedDateTo,
			}),
		enabled: !!idFirm && !!debouncedDateFrom && !!debouncedDateTo,
		staleTime: 1000 * 60 * 15,
		gcTime: 1000 * 60 * 30,
	});

	// Obliczanie sum do podsumowania
	const summaryData = useMemo(() => {
		if (!reportData.length) return {};
		const sums = reportData.reduce(
			(acc, row) => ({
				sprzIlospLacznie: (acc.sprzIlospLacznie || 0) + (row.sprzIlospLacznie || 0),
				sprzNettoLacznie: (acc.sprzNettoLacznie || 0) + (row.sprzNettoLacznie || 0),
				warZakNettoLacznie: (acc.warZakNettoLacznie || 0) + (row.warZakNettoLacznie || 0),
				marzaLacznie: (acc.marzaLacznie || 0) + (row.marzaLacznie || 0),
			}),
			{} as Record<string, number>,
		);

		return {
			sprzIlospLacznie: Number(sums.sprzIlospLacznie?.toFixed(2) || 0),
			sprzNettoLacznie: Number(sums.sprzNettoLacznie?.toFixed(2) || 0),
			warZakNettoLacznie: Number(sums.warZakNettoLacznie?.toFixed(2) || 0),
			marzaLacznie: Number(sums.marzaLacznie?.toFixed(2) || 0),
		};
	}, [reportData]);

	const formatCurrency = (val: number) => `${val.toFixed(2)} zł`;

	// Kontrolki budowane raz: desktop w pasku (bez zmian), telefon w arkuszu filtrow.
	const pharmacySelector = () => (
		<PharmacySelector
			value={idFirm}
			onChange={(val, name) => {
				setIdFirm(val);
				setPharmacyName(name);
			}}
		/>
	);
	const dateInput = (which: "from" | "to", className: string, ariaLabel?: string) => (
		<Input
			type="date"
			className={className}
			value={which === "from" ? dateFrom : dateTo}
			onChange={(e) => (which === "from" ? setDateFrom(e.target.value) : setDateTo(e.target.value))}
			aria-label={ariaLabel}
		/>
	);
	const mobilePharmacyField = () => (
		<MobileFilterField label="Apteka">{pharmacySelector()}</MobileFilterField>
	);
	const mobileDateFields = () => (
		<div className="grid grid-cols-2 gap-2">
			<MobileFilterField label="Data od">
				{dateInput("from", "h-10 w-full text-sm", "Data od")}
			</MobileFilterField>
			<MobileFilterField label="Data do">
				{dateInput("to", "h-10 w-full text-sm", "Data do")}
			</MobileFilterField>
		</div>
	);
	const defaultDateFrom = format(firstDayOfMonth, "yyyy-MM-dd");
	const defaultDateTo = format(today, "yyyy-MM-dd");
	const mobileChips: MobileFilterChip[] = isBelowMd
		? [
				{
					id: "pharmacy",
					label: "Apteka",
					value: pharmacyName ?? (idFirm ? `Apteka ${idFirm}` : "Wybierz aptekę"),
					active: idFirm != null,
					picker: mobilePharmacyField(),
				},
				{
					id: "dates",
					label: "Zakres dat",
					value: dateRangeLabel(dateFrom, dateTo),
					active: dateFrom !== defaultDateFrom || dateTo !== defaultDateTo,
					picker: mobileDateFields(),
				},
			]
		: [];

	return (
		<div className="flex flex-col flex-1 min-h-0 min-w-0 w-full p-3 sm:p-6 gap-3 sm:gap-4 bg-muted/10">
			<div className="flex flex-col gap-2 min-w-0 shrink-0">
				<PageHeader
					title="Rozkład marż"
					description="Szczegółowa analiza marżowości produktów z podziałem na rodzaje (RX, RX100, OTC)"
				/>

				<MobileFilters
					className={PADDED_FILTER_BAR_CLASS}
					chips={mobileChips}
					sheetContent={
						<>
							{mobilePharmacyField()}
							{mobileDateFields()}
						</>
					}
					onClear={() => {
						setIdFirm(null);
						setPharmacyName(null);
						setDateFrom(defaultDateFrom);
						setDateTo(defaultDateTo);
					}}
					resultLabel={
						idFirm == null
							? "Gotowe"
							: showCountLabel(
									isFetching ? null : reportData.length,
									"pozycję",
									"pozycje",
									"pozycji",
								)
					}
					actions={
						isFetching ? (
							<Loader2
								className="size-4 shrink-0 animate-spin text-muted-foreground"
								aria-label="Pobieranie danych"
							/>
						) : null
					}
				>
					<div className="flex flex-wrap items-center gap-3 mt-2 min-w-0">
						<div className="w-64 max-w-full">{pharmacySelector()}</div>

						<div className="flex items-center gap-1.5 bg-background border rounded-md p-1 shadow-sm shrink-0">
							<Calendar className="size-4 text-muted-foreground ml-2" />
							{dateInput("from", DESKTOP_DATE_INPUT_CLASS)}
							<span className="text-muted-foreground px-1 text-sm">—</span>
							{dateInput("to", DESKTOP_DATE_INPUT_CLASS)}
						</div>

						<div className="ml-auto flex items-center gap-3">
							{isFetching && (
								<span className="text-xs text-muted-foreground animate-pulse font-medium ml-2">
									Pobieranie danych...
								</span>
							)}
						</div>
					</div>
				</MobileFilters>

				{/* Telefon: bez apteki raport jest pusty — wskazowka zamiast pustej listy. */}
				{isBelowMd && idFirm == null && (
					<p className="text-sm text-muted-foreground">
						Wybierz aptekę, aby zobaczyć rozkład marż.
					</p>
				)}
			</div>

			{/* Kluczowe: min-h-0, min-w-0 na kontenerze umozliwiaja dzialanie overflow-auto wewnatrz DataTable */}
			<div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden rounded-lg border bg-card shadow-sm w-full relative">
				<DataTable
					tableId="report-margin-distribution"
					columns={columns}
					data={reportData}
					className="flex-1 min-h-0 min-w-0 w-full"
					enableToolbar
					enableColumnVisibility
					enableSchemes
					enableColumnPinning
					enableGrouping
					showAggregationOptions
					showSummaryRow
					summableColumns={[
						"sprzIlospLacznie",
						"sprzNettoLacznie",
						"warZakNettoLacznie",
						"marzaLacznie",
					]}
					summaryData={summaryData}
					summaryQuickView={[
						{ columnId: "sprzIlospLacznie", label: "Σ Ilość" },
						{ columnId: "sprzNettoLacznie", label: "Σ Sprzedaż" },
						{ columnId: "marzaLacznie", label: "Σ Marża" },
					]}
					summaryFormatter={(col, value) =>
						col === "sprzIlospLacznie" ? value.toFixed(2) : formatCurrency(value)
					}
					enableExport
					exportFileName={`Rozklad_marz_${idFirm}_${dateFrom}_${dateTo}`}
					fetchAllForExport={async () => reportData as unknown as Record<string, unknown>[]}
				/>
			</div>
		</div>
	);
}
